import { getPercent } from "app/UtilitiesAndConstants/UtilityFunctions/statistics";
import type { IMeteringData, IMeteringState } from "./types";
import { ELimitationType, EMeteringCountRatio } from "./types";

const CUSTOM_15_000_SOFT_LIMITATION = 15_000;

export const getMeteringState = (data: IMeteringData): IMeteringState => {
    const { count = 0, isAdmin, allowedNum, allowedHardLimitation } = data;
    const percent = getPercent(count, allowedNum);

    // get the text state according to assets in the account and admin status:
    if (allowedHardLimitation) {
        return {
            textState: getHardCaseState({ isAdmin, percent, allowedNum, count }),
            limitationType: ELimitationType.HARD,
        };
    } else {
        return {
            textState: getSoftCaseState({ isAdmin, percent, allowedNum }),
            limitationType: ELimitationType.SOFT,
        };
    }
};

const getSoftCaseState = ({ isAdmin, percent, allowedNum }) => {
    const {
        ADMIN_BELOW_80,
        ADMIN_BELOW_100,
        ADMIN_EQUAL_100,
        ADMIN_ABOVE_100,
        BELOW_80,
        BELOW_100,
        EQUAL_100,
        ABOVE_100,
        CUSTOM_15_000,
    } = EMeteringCountRatio;
    if (allowedNum === CUSTOM_15_000_SOFT_LIMITATION) {
        return CUSTOM_15_000;
    }
    if (isAdmin) {
        if (percent < 80) {
            return ADMIN_BELOW_80;
        } else if (percent < 100) {
            return ADMIN_BELOW_100;
        } else if (percent === 100) {
            return ADMIN_EQUAL_100;
        } else {
            return ADMIN_ABOVE_100;
        }
    } else {
        if (percent < 80) {
            return BELOW_80;
        } else if (percent < 100) {
            return BELOW_100;
        } else if (percent === 100) {
            return EQUAL_100;
        } else {
            return ABOVE_100;
        }
    }
};

const getHardCaseState = ({ isAdmin, percent, allowedNum, count }) => {
    const {
        HARD_ADMIN_BELOW_80,
        HARD_ADMIN_BELOW_100,
        HARD_ADMIN_EQUAL_100,
        HARD_ADMIN_EQUAL_100_PLUS_1,
        HARD_ADMIN_ABOVE_100_PLUS_2,
        HARD_BELOW_80,
        HARD_BELOW_100,
        HARD_EQUAL_100,
        HARD_EQUAL_100_PLUS_1,
        HARD_ABOVE_100_PLUS_2,
    } = EMeteringCountRatio;
    if (isAdmin) {
        if (percent < 80) {
            return HARD_ADMIN_BELOW_80;
        } else if (percent < 100) {
            return HARD_ADMIN_BELOW_100;
        } else if (percent === 100) {
            return HARD_ADMIN_EQUAL_100;
        } else if (count === allowedNum + 1) {
            return HARD_ADMIN_EQUAL_100_PLUS_1;
        } else {
            return HARD_ADMIN_ABOVE_100_PLUS_2;
        }
    } else {
        if (percent < 80) {
            return HARD_BELOW_80;
        } else if (percent < 100) {
            return HARD_BELOW_100;
        } else if (percent === 100) {
            return HARD_EQUAL_100;
        } else if (count === allowedNum + 1) {
            return HARD_EQUAL_100_PLUS_1;
        } else {
            return HARD_ABOVE_100_PLUS_2;
        }
    }
};

export const isLimitationExceeded = (data: IMeteringData | undefined): boolean => {
    if (!data || !data.allowedHardLimitation) {
        return false;
    }

    const { count = 0, allowedNum } = data;
    // exceeded happens when the count is above the allowed number + 1
    return count > allowedNum + 1;
};
